import { useCallback, useEffect, useState } from "react";
import { DeleteTwoTone, EditTwoTone, EyeOutlined } from "@ant-design/icons";
import { message, Typography, Dropdown, Menu, Popconfirm, Modal } from "antd";
import ListTable from "../../../common/ListTable";
import { useLocation } from "react-router";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ExtraContent from "../CommonComponents/ExtraContent";
import FilterComponent from "../CommonComponents/FilterComponent";
import { getData, deleteData } from "../../../apiServices/common";
import { AIR_HOLE_TYPE_LIST_ENDPOINT, AIR_HOLE_TYPE_DELETE_ENDPOINT } from "../../../apiServices/API_ENDPOINTS";
import { useSelector } from "react-redux";
import { hasPermission } from "../../../utils/functions";
import {
  AIR_HOLE_TYPE_CREATE_PERMISSION,
  AIR_HOLE_TYPE_EDIT_PERMISSION,
  AIR_HOLE_TYPE_VIEW_PERMISSION,
  AIR_HOLE_TYPE_DELETE_PERMISSION,
} from "../../../routes/permissions";
import AirHoleTypeForm from "./AirHoleTypeForm";

const AirHoleTypeConfig = () => {
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isView, setIsView] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedItem, setSelectedItem] = useState(null);

  const { Title } = Typography;
  const filterData = useSelector(({ filter }) => filter.filterData);

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};
    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllAirHoleTypes = useCallback(
    async (filterValues) => {
      const { name } = filterValues || {};
      setLoading(true);
      setFetchError(null);
      const query = `${AIR_HOLE_TYPE_LIST_ENDPOINT}?page=${currentPage}`;
      const bodyData = {
        ...(name && { name }),
      };
      const response = await getData(query, false, bodyData);

      if (response?.data?.code === 200) {
        setLoading(false);
        const data = response?.data?.data;
        setMetaData({
          defaultPageSize: data?.per_page,
          current: data?.current_page,
          defaultCurrent: data?.current_page,
          total: data?.total,
        });
        setTableData(data?.data ?? []);
      } else {
        setLoading(false);
        setFetchError("Could not load this list.");
        message.error("Error loading air hole types");
      }
    },
    [currentPage],
  );

  useEffect(() => {
    getAllAirHoleTypes(filterData);
  }, [refresh, filterData, getAllAirHoleTypes]);

  const onModalOpen = () => {
    setIsEdit(false);
    setIsView(false);
    setSelectedItem(null);
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    setIsModalOpen(false);
    setIsEdit(false);
    setIsView(false);
    setSelectedItem(null);
  };

  const refetch = () => {
    setRefresh((prev) => prev + 1);
  };

  const deleteSelected = async (id) => {
    setLoading(true);
    const response = await deleteData(`${AIR_HOLE_TYPE_DELETE_ENDPOINT}/${id}`);
    if (response?.data?.code === 200 || response?.code === 200) {
      setLoading(false);
      message.success("Air Hole Type deleted successfully");
      refetch();
    } else {
      setLoading(false);
      message.error(response?.data?.message || "Something went wrong");
    }
  };

  const openModalForView = (record) => {
    setIsView(true);
    setIsEdit(false);
    setSelectedItem(record);
    setIsModalOpen(true);
  };

  const openModalForEdit = (record) => {
    setIsEdit(true);
    setIsView(false);
    setSelectedItem(record);
    setIsModalOpen(true);
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    refetch();
  };

  const getModalTitle = () => {
    if (isView) return "View Air Hole Type";
    if (isEdit) return "Edit Air Hole Type";
    return "Create Air Hole Type";
  };

  const tableColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "25%",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "40%",
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "15%",
      render: (status) => <span>{status ? "Active" : "Inactive"}</span>,
    },
    {
      title: "Action",
      key: "action",
      width: "20%",
      render: (_, record) => (
        <Dropdown.Button
          overlay={
            <Menu
              items={[
                {
                  label: (
                    <span
                      onClick={() => openModalForView(record)}
                      style={{ display: "flex", alignItems: "center", gap: 5 }}
                    >
                      <EyeOutlined />
                      View
                    </span>
                  ),
                  key: "view",
                  disabled: !hasPermission([AIR_HOLE_TYPE_VIEW_PERMISSION]),
                },
                {
                  label: (
                    <span
                      onClick={() => openModalForEdit(record)}
                      style={{ display: "flex", alignItems: "center", gap: 5 }}
                    >
                      <EditTwoTone />
                      Edit
                    </span>
                  ),
                  key: "edit",
                  disabled: !hasPermission([AIR_HOLE_TYPE_EDIT_PERMISSION]),
                },
                {
                  label: (
                    <Popconfirm title="Are you sure you want to delete?" onConfirm={() => deleteSelected(record?.id)}>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          color: "red",
                        }}
                      >
                        <DeleteTwoTone twoToneColor="red" />
                        Delete
                      </span>
                    </Popconfirm>
                  ),
                  key: "delete",
                  disabled: !hasPermission([AIR_HOLE_TYPE_DELETE_PERMISSION]),
                },
              ]}
            />
          }
        />
      ),
    },
  ];

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={onModalOpen}
                disabled={!hasPermission([AIR_HOLE_TYPE_CREATE_PERMISSION])}
                addButtonText={hasPermission([AIR_HOLE_TYPE_CREATE_PERMISSION]) ? "Add New" : "No Permission"}
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Air Hole Type Configuration</Title>}
          />
        }
        content={
          <>
            <FilterComponent
              tableData={tableData}
              filterAPI={getAllAirHoleTypes}
              showFilter={showFilter}
              setShowFilter={setShowFilter}
            />
            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllAirHoleTypes(filterData)}
                emptyText="No records to show yet."
                dataSource={tableData}
                columns={tableColumns}
                rowKey="id"
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: changePage,
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
              />
            </div>
          </>
        }
      />
      <Modal
        open={isModalOpen}
        title={getModalTitle()}
        onCancel={onCloseModal}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        footer={null}
      >
        <AirHoleTypeForm
          isEdit={isEdit}
          isView={isView}
          onCloseModal={onCloseModal}
          refetch={refetch}
          selectedItem={selectedItem}
        />
      </Modal>
    </>
  );
};

export default AirHoleTypeConfig;
